import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApiService } from './api.service';
import { LocalService } from './local.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly enabledKey = 'push-enabled';
  private readonly deviceIdKey = 'push-device-id';

  constructor(
    private localService: LocalService,
    private apiService: ApiService,
  ) {}

  async isEnabled(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    const subscription = await this.getSubscription();
    const enabled = !!subscription && Notification.permission === 'granted';
    await this.localService.saveDataV2(this.enabledKey, enabled);
    return enabled;
  }

  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      !!environment.webPushPublicKey
    );
  }

  getPermissionStatus(): NotificationPermission | 'unsupported' {
    if (!this.isSupported()) {
      return 'unsupported';
    }

    return Notification.permission;
  }

  async enable(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      await this.localService.saveDataV2(this.enabledKey, false);
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        (await registration.pushManager.getSubscription()) ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(
            environment.webPushPublicKey,
          ),
        }));

      await this.apiService.request('/notifications/subscribe', 'POST', {
        subscription: subscription.toJSON(),
        enabled: true,
        deviceId: await this.getOrCreateDeviceId(),
        userAgent: navigator.userAgent,
      });

      await this.localService.saveDataV2(this.enabledKey, true);
      return true;
    } catch {
      await this.localService.saveDataV2(this.enabledKey, false);
      return false;
    }
  }

  async disable(): Promise<void> {
    const subscription = await this.getSubscription();

    try {
      await this.apiService.request('/notifications/unsubscribe', 'POST', {
        endpoint: subscription?.endpoint,
        subscription: subscription?.toJSON(),
        enabled: false,
      });
    } catch {
      // Backend endpoint can be enabled later without breaking frontend flow.
    }

    if (subscription) {
      try {
        await subscription.unsubscribe();
      } catch {
        // Ignore browser-side unsubscribe failures and clear local state anyway.
      }
    }

    await this.localService.saveDataV2(this.enabledKey, false);
  }

  private async getSubscription(): Promise<PushSubscription | null> {
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      !window.isSecureContext
    ) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      return registration.pushManager.getSubscription();
    } catch {
      return null;
    }
  }

  private async getOrCreateDeviceId(): Promise<string> {
    const storedId = await this.localService.getDataV2<string>(this.deviceIdKey);

    if (storedId) {
      return storedId;
    }

    const deviceId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `device-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

    await this.localService.saveDataV2(this.deviceIdKey, deviceId);
    return deviceId;
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = `${base64String}${padding}`
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let index = 0; index < rawData.length; index++) {
      outputArray[index] = rawData.charCodeAt(index);
    }

    return outputArray;
  }
}
